import { BENEFICIARY_STATUS } from "../../../domain/beneficiary.enum";

/**
 * SIMS-9111 — the re-enrollment consent gate.
 *
 * Two questions have to agree about the consent document, and before this ticket they
 * did not:
 *
 *   1. Does the schema REQUIRE a consent file?  (isConsentFormRequired -> isNewReenroll)
 *   2. May the user ATTACH one?                 (isDisableConsentForm -> UploadButton)
 *
 * They were computed from different sources. (1) used a locally-derived `isNewEnrollment`,
 * while (2) read `realLastEnrollment.isNewEnrollment` — a field neither backend populates
 * on `realLastEnrollment` (it is only ever $addFields-ed onto `lastEnrollment`, and is
 * absent from the stored enrollment documents). That read was permanently `undefined`, so
 * the Upload button was disabled on every re-enrollment, while validation still demanded
 * the file whenever the program required consent: an unsatisfiable form.
 *
 * Both answers are derived here, from one input, so they cannot drift apart again.
 */

/** The enrollment fields this gate needs. Mirrors the populated shape of EnrollmentData. */
export interface ConsentGateEnrollment {
	status?: string;
	endDate?: Date | string | null;
}

/**
 * Treat the previous enrollment as "new" — i.e. far enough removed that re-consent is
 * warranted — when it completed, or when it ended more than `staleAfterDays` ago.
 *
 * `status` is read from the enrollment rather than a bare identifier: the original code
 * referenced an undeclared `status`, which silently resolved to the DOM global
 * `window.status` (always ""), so the COMPLETED branch could never be true.
 */
export const isNewEnrollmentFor = (
	enrollment: ConsentGateEnrollment | undefined,
	now: Date = new Date(),
	staleAfterDays = 30,
): boolean => {
	if (enrollment?.status === BENEFICIARY_STATUS.COMPLETED) {
		return true;
	}
	if (!enrollment?.endDate) {
		return false;
	}
	const endDate = new Date(enrollment.endDate);
	if (Number.isNaN(endDate.getTime())) {
		return false;
	}
	const staleBefore = new Date(now);
	staleBefore.setDate(staleBefore.getDate() - staleAfterDays);
	return endDate.getTime() < staleBefore.getTime();
};

/**
 * Whether a consent document is mandatory. Registering always requires one when the
 * program is configured for consent; re-enrolling requires one only for a "new" enrollment.
 */
export const isConsentRequired = (
	programRequiresConsent: boolean | undefined,
	isRegister: boolean,
	isNewEnrollment: boolean,
): boolean => {
	if (!programRequiresConsent) {
		return false;
	}
	return isRegister || isNewEnrollment;
};

/**
 * Whether the upload control is disabled.
 *
 * THE INVARIANT: this must never be true while `isConsentRequired` is true, or the form
 * cannot be completed. `isConsentDisabled` is the single place that decides it, and
 * consentGateHolds() below is asserted over the whole input space in the tests.
 */
export const isConsentDisabled = (
	isRegister: boolean,
	consentRequired: boolean,
): boolean => !isRegister && !consentRequired;

/**
 * The invariant itself, exported so tests (and any future caller) can assert it directly:
 * a required consent form is always attachable.
 */
export const consentGateHolds = (
	isRegister: boolean,
	consentRequired: boolean,
): boolean =>
	!(consentRequired && isConsentDisabled(isRegister, consentRequired));
